<!--
  Engine diagnostics for the live marker popup. Mirrors the Flutter app's
  _ObdDtcSection so the same car says the same thing on both.

  Shown for every OBD vehicle, not only faulty ones: "no faults reported" is the
  answer an owner actually wants, and a section that only ever appears alongside
  bad news trains people to dread opening it. Gated on obd_present, because only
  a car with the tracker in its OBD port can report any of this.

  The code list is fetched on demand from GET /assets/{id}/dtcs when the popup
  opens. That endpoint firstOrFail()s, so a car that has never reported a row
  404s - which is the ordinary state of a car with nothing wrong, not an error.
-->
<template>
    <div v-if="obd.obdPresent && obd.isVehicle && !obd.isPhone" class="obd-section" :style="{borderColor: toneBorder, background: toneBg}">
        <div class="obd-title" :style="{color: tone}">
            <v-icon icon="mdi-engine" size="14" class="me-1"/>Engine diagnostics
        </div>

        <div v-if="loading" class="obd-hint">
            <v-progress-circular :size="14" :width="2" indeterminate color="orange" class="me-2"/>
            Checking...
        </div>
        <div v-else-if="error" class="obd-hint">{{ error }}</div>

        <template v-else-if="data">
            <!-- ONE status row, not a lamp line plus three count chips. "3
                 stored, 1 pending" is a scan-tool readout, not an answer. -->
            <div class="obd-status" :style="{color: tone}">
                <v-icon :icon="statusIcon" size="13" class="me-1"/>
                <span>{{ headline }}</span>
            </div>

            <!-- The flashing lamp is the real "stop driving" signal and no scan
                 tool can read it, so on anything urgent we ask the driver to
                 look at their own dashboard rather than pretend we know. -->
            <div v-if="severity === 'urgent'" class="obd-blink">{{ blinkingLampWarning }}</div>

            <div v-for="dtc in visibleCodes" :key="dtc.code" class="obd-code">
                <div class="d-flex align-center ga-2">
                    <v-chip size="x-small" color="orange" variant="flat" class="font-weight-bold" style="font-family: monospace;">
                        {{ dtc.code }}
                    </v-chip>
                    <span class="obd-code-desc">{{ dtc.plain }}</span>
                </div>
                <!-- What to do about it. This used to render the server's
                     suggested_fix beside a green wrench, which reads as advice
                     from a mechanic - it is unreviewed language-model output. -->
                <div class="obd-fix">
                    <v-icon icon="mdi-lightbulb-outline" size="x-small" class="me-1"/>{{ dtc.action }}
                </div>
            </div>

            <!-- Never omitted. An OBD read happens on the tracker's own
                 schedule, so a bare "no faults" would claim more than we know. -->
            <div class="obd-checked">{{ checkedLabel }}</div>
        </template>
    </div>
</template>

<script setup>
import {ref, computed, watch} from "vue";
import {send} from "@/composables/apiClient";
import {obdStatus} from "@/composables/useObdStatus";
import {formatDateTime} from "@/composables/useTimezone";
import {
    lookupDtc, overallSeverity, severityHeadline, severityColor,
    BLINKING_LAMP_WARNING,
} from "@/composables/dtcCatalog";

const blinkingLampWarning = BLINKING_LAMP_WARNING;

const props = defineProps({
    device: {type: Object, required: true},
    // True while this device's popup is open, so we only fetch on demand.
    active: {type: Boolean, default: false},
});

const obd = computed(() => obdStatus(props.device));
const loading = ref(false);
const error = ref(null);
const data = ref(null);

const fetchDtcs = () => {
    const assetId = props.device?.asset_id;
    if (!assetId || loading.value) return;
    loading.value = true;
    error.value = null;
    send('get', `assets/${assetId}/dtcs`)
        .then((response) => {
            const d = response?.data?.data;
            if (d) data.value = d;
            else error.value = 'Could not load diagnostic codes.';
        })
        .catch((err) => {
            // A car that has never reported a DTC row 404s, because the endpoint
            // firstOrFail()s. That is not an error to show the owner - it is the
            // ordinary state of a car with nothing wrong, and reporting it as a
            // failure is what kept the reassuring state hidden.
            if (err?.response?.status === 404) data.value = {};
            else error.value = 'Could not load diagnostic codes.';
        })
        .finally(() => {
            loading.value = false;
        });
};

// Codes of one group out of the decoded payload. The API keys `description` by
// the type name plus ' DTCs'.
const codesOf = (type) => {
    const list = data.value?.description?.[`${type} DTCs`];
    return Array.isArray(list) ? list.map((d) => String(d?.code ?? '')).filter(Boolean) : [];
};

// Pending codes deliberately excluded: the car saw something once and is not yet
// convinced, and treating that as a fault manufactures worry.
const severity = computed(() => overallSeverity({
    milOn: obd.value.mil,
    stored: codesOf('Stored'),
    permanent: codesOf('Permanent'),
}));

const headline = computed(() => severityHeadline(severity.value));

// Only claim a state once we know one.
//
// While loading and after a failure there are no codes, so severity resolves to
// allGood for the common lamp-off car and the whole frame rendered GREEN - an
// all-clear on data we did not have, and it stayed green behind the error text.
// Neutral amber until the load actually settles, matching the Flutter original.
const settled = computed(() => !loading.value && !error.value && data.value !== null);
const tone = computed(() => (settled.value ? severityColor(severity.value) : '#F97316'));
const toneBorder = computed(() => `${tone.value}66`);
const toneBg = computed(() => `${tone.value}0F`);

const statusIcon = computed(() => ({
    allGood: 'mdi-check-circle-outline',
    watch: 'mdi-information-outline',
    urgent: 'mdi-alert-circle-outline',
}[severity.value] ?? 'mdi-alert'));

// Stored and permanent only, each rendered through the hand-written catalogue.
// The server also sends user_description and suggested_fix, but every row in
// dtc_codes is language-model output with no human review.
const visibleCodes = computed(() => {
    const seen = new Set();
    const out = [];
    for (const type of ['Stored', 'Permanent']) {
        for (const code of codesOf(type)) {
            if (seen.has(code)) continue;
            seen.add(code);
            const info = lookupDtc(code);
            out.push({code, plain: info.plain, action: info.action});
        }
    }
    return out;
});

const checkedLabel = computed(() => {
    const at = data.value?.event_datetime;
    const when = at ? formatDateTime(at) : '';
    return when ? `Checked ${when}` : 'This car has not reported a diagnostic read yet.';
});

// Fetch the first time the popup opens for an OBD vehicle. Re-fetch is avoided
// once data is loaded for this instance.
watch(
    () => props.active && obd.value.obdPresent && obd.value.isVehicle && !obd.value.isPhone,
    (go) => {
        // Retry on reopen after a failure. These components stay mounted inside
        // the map's v-for, so without this one blip left that car's popup stuck
        // on "Could not load diagnostic codes." for the rest of the session -
        // and now that the section renders for every OBD vehicle rather than
        // only faulty ones, that is a lot more cars.
        if (go && !data.value && !loading.value) fetchDtcs();
    },
    {immediate: true},
);

// A live MIL or DTC update means the cached read is out of date. Drop it so the
// section asks again, rather than showing a headline that has moved on from the
// codes listed under it and the "Checked ..." time beneath those.
watch(
    () => [props.device?.indication_lamp_status, props.device?.dtc_count],
    () => {
        if (loading.value) return;
        data.value = null;
        error.value = null;
        if (props.active) fetchDtcs();
    },
);
</script>

<style scoped>
/* Border and background are bound inline: the frame takes the severity's
   colour, so a clean car reads green rather than permanently amber. */
.obd-section {
    margin-top: 8px;
    padding: 8px 10px;
    border: 1px solid rgba(249, 115, 22, 0.4);
    border-radius: 8px;
    background: rgba(249, 115, 22, 0.06);
}
.obd-title {
    font-weight: 700;
    font-size: 12.5px;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}
.obd-status {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 4px;
}
.obd-blink {
    font-size: 11px;
    font-weight: 600;
    color: #d70000;
    margin-bottom: 4px;
}
.obd-checked {
    font-size: 11px;
    color: #757575;
    margin-top: 6px;
}
.obd-hint {
    display: flex;
    align-items: center;
    font-size: 11.5px;
    color: #757575;
}
.obd-code {
    margin-bottom: 4px;
}
.obd-code-desc {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
}
/* Amber, not green. A green wrench read as a mechanic endorsing the fix. */
.obd-fix {
    font-size: 11px;
    color: #b45309;
    margin-top: 1px;
    display: flex;
    align-items: flex-start;
}
</style>
